import { faChevronLeft, faChevronRight, faSearchPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { ReactImageGalleryItem } from "react-image-gallery";

import { GalleryPhotoOrigin } from "@/mk2/apps/wiki/components/GalleryPhotoOrigin";
import { Props } from "@/mk2/apps/wiki/containers/ArticlePhoto/ArticlePhotoPage";
import {
    ArticleGalleryItem,
    buildArticleGalleryPhotos,
    galleryItemLinkTo,
    isMergedScope,
} from "@/mk2/apps/wiki/gallery";
import { wikiArticlePhotoUrl } from "@/mk2/apps/wiki/urls";
import { ImageGalleryLoadable } from "@/mk2/components/ImageGalleryLoadable";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { cacheLast } from "@/mk2/helpers/cache";
import { url } from "@/mk2/helpers/urls";
import { photoUrl } from "@/mk/photo/photoUrl";

import styles from "./ArticlePhoto.mscss";
import "./ArticlePhoto.scss";

export class ArticlePhoto extends React.Component<Props> {
    private galleryItems = cacheLast<ArticleGalleryItem[]>(true);

    public render() {
        const { loadingState, article } = this.props;

        return (
            <div className={styles.ArticlePhoto}>
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderSuccess={this.renderSuccess}
                    hasData={Boolean(article)}
                />
            </div>
        );
    }

    // Reached from the merged product row? Then navigate the whole merged gallery
    // (article + all approved experience photos). Reached scoped (e.g. the cover)?
    // Then stay within the article's own photos.
    private isMerged(): boolean {
        return isMergedScope(this.props.location.search);
    }

    private getGalleryItems(): ArticleGalleryItem[] {
        const { article } = this.props;
        const merged = this.isMerged();
        return this.galleryItems(
            () =>
                merged
                    ? buildArticleGalleryPhotos(article)
                    : (article.photos ?? []).map((p) => ({
                          photofile: p,
                          experienceId: null as number | null,
                      })),
            merged,
            article.photos,
            article.experiences,
        );
    }

    private renderSuccess = () => {
        const { articlePhoto } = this.props;
        const items = this.getGalleryItems();

        const currentIndex: number = items.findIndex(
            (item) => item.photofile.id === articlePhoto.id,
        );

        const galleryPhotos: ReactImageGalleryItem[] = items.map(({ photofile }) => ({
            original: photoUrl(photofile, "s738x738"),
            thumbnail: photoUrl(photofile, "s150x150"),
            fullSizeUrl: photoUrl(photofile, "s1600x1600"),
        }));

        return (
            <>
                <div className={styles.ArticlePhoto__gallery}>
                    <ImageGalleryLoadable
                        items={galleryPhotos}
                        lazyLoad
                        showIndex
                        showThumbnails={false}
                        autoPlay={false}
                        showPlayButton={false}
                        showFullscreenButton={false}
                        slideDuration={500}
                        startIndex={currentIndex}
                        onSlide={this.onSlide}
                        renderLeftNav={this.renderLeftNav}
                        renderRightNav={this.renderRightNav}
                        renderCustomControls={this.renderCustomControls}
                    />
                </div>
            </>
        );
    };

    private renderLeftNav = (onClick, disabled) => {
        return (
            <div
                key="prev"
                className={styles.ArticlePhoto__prev}
                onClick={disabled ? undefined : onClick}
            >
                <FontAwesomeIcon icon={faChevronLeft} className={styles.ArticlePhoto__prev__icon} />
            </div>
        );
    };

    private renderRightNav = (onClick, disabled) => {
        return (
            <div
                key="next"
                className={styles.ArticlePhoto__next}
                onClick={disabled ? undefined : onClick}
            >
                <FontAwesomeIcon
                    icon={faChevronRight}
                    className={styles.ArticlePhoto__next__icon}
                />
            </div>
        );
    };

    private renderCustomControls = () => {
        const { article, photoId } = this.props;
        const item = this.getGalleryItems().find((i) => i.photofile.id === photoId);
        if (!item) {
            return null;
        }
        const showMagnifier = item.photofile?.versions?.includes("s1600x1600");

        return (
            <>
                <GalleryPhotoOrigin article={article} item={item} />
                {showMagnifier && (
                    <div className={styles.ArticlePhoto__controls}>
                        <a
                            data-google-interstitial="false"
                            key="magnifier"
                            href={photoUrl(item.photofile, "s1600x1600")}
                            target="_blank"
                            className={styles.ArticlePhoto__controls__control}
                            rel="noopener"
                        >
                            <FontAwesomeIcon icon={faSearchPlus} />
                        </a>
                    </div>
                )}
            </>
        );
    };

    private onSlide = (currentIndex: number) => {
        const { article, routeNavigate, routeParams } = this.props;
        const item = this.getGalleryItems()[currentIndex];
        if (!item) {
            return;
        }
        if (this.isMerged()) {
            routeNavigate(galleryItemLinkTo(article, item), { replace: true });
            return;
        }
        routeNavigate(
            url(wikiArticlePhotoUrl, {
                categorySlug: routeParams.categorySlug,
                articleSlug: routeParams.articleSlug,
                photoId: item.photofile.id,
            }),
            { replace: true },
        );
    };

    private load = () => {
        const {
            routeParams: { categorySlug, articleSlug },
            photoId,
            onLoad,
        } = this.props;
        onLoad(articleSlug, categorySlug, photoId);
    };
}
